import { Button, Stack, Tooltip, Typography } from '@mui/material';
import {
  addCampaignItemsSchema,
  brandCampaignItemSchema,
  BrandCampaignMetadata,
  CampaignItemsBaseColumns,
  useGetBrandCampaignItemsColumns,
} from '@retail/campaign/components';
import {
  BrandCampaign,
  BrandCampaignItem,
  BrandCampaignPatchItemDTO,
} from '@retail/campaign/types';
import { useCallback, useMemo } from 'react';
import { Image } from '@retail/components';
import { useAppTFunction } from '@retail/app/i18n';
import {
  useBrandCampaignDetailsActions,
  useUpdateCampaignItemsActions,
} from '@retail/campaign/context';
import { CampaignDetailsContainerBase } from './CampaignDetailsContainerBase';
import { useUserBrands } from '@retail/app/stores/selected-context';
import { brandLogo } from '@retail/utils';
import { useFetchBrandCampaignItems } from '@retail/campaign/data-access';
import { useTranslation } from 'react-i18next';
import { useDisclosure } from '@shared/hooks';
import { BrandCampaignDetailsAddItemsDialog } from './AddItems';
import { UniversalFormProvider } from '@shared/components';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import AddIcon from '@mui/icons-material/Add';
import { BrandEditCampaignItemsDialog } from './BrandEditCampaignItemsDialog';

interface Props {
  campaign: BrandCampaign;
}

export const BrandCampaignDetailsContainer = ({ campaign }: Props) => {
  const t = useAppTFunction();
  const { availableBrandsForChosenTenant, selectedBrand } = useUserBrands();
  const showPackageDiscounts = selectedBrand.wholesaler;
  const { i18n } = useTranslation();
  const { isOpen: includeVat, onToggle: toggleIncludeVat } = useDisclosure(true);

  const {
    data: campaignItemsDto,
    status,
    refetch,
  } = useFetchBrandCampaignItems({
    campaignId: campaign.id,
    suspense: false,
  });
  const campaignItems = useMemo(
    () => campaignItemsDto?.map((dto) => BrandCampaignItem.fromDto(dto, i18n.language)),
    [campaignItemsDto, i18n.language]
  );

  const { handleUpdateItem } = useUpdateCampaignItemsActions<BrandCampaignPatchItemDTO>({
    campaignId: campaign.id,
    type: 'brand',
  });

  const handleSubmitSalesPrice = useCallback(
    (item: BrandCampaignItem, salesPrice: number) =>
      handleUpdateItem(item.id, {
        ...item.getPatchCampaignItem(),
        campaignSalesPrice: salesPrice,
      }),
    [handleUpdateItem]
  );

  const handleSubmitPurchasePrice = useCallback(
    (item: BrandCampaignItem, purchasePrice: number) =>
      handleUpdateItem(item.id, {
        ...item.getPatchCampaignItem(),
        campaignPurchasePrice: purchasePrice,
      }),
    [handleUpdateItem]
  );

  const handleSubmitCampaignUnit = useCallback(
    (item: BrandCampaignItem, campaignUnit: string) =>
      handleUpdateItem(item.id, {
        ...item.getPatchCampaignItem(),
        campaignUnit,
      }),
    [handleUpdateItem]
  );

  const handleSubmitPurchasePeriod = useCallback(
    (item: BrandCampaignItem, purchasePeriodFrom?: string, purchasePeriodTo?: string) =>
      handleUpdateItem(item.id, {
        ...item.getPatchCampaignItem(),
        purchasePeriodFrom,
        purchasePeriodTo,
      }),
    [handleUpdateItem]
  );

  const brandColumns = useGetBrandCampaignItemsColumns({
    handleUpdateIsSalesPriceOverridable: campaign.isCampaignEditable()
      ? ({ isSalesPriceOverridable, itemId }) => {
          const campaignItem = campaignItems?.find((item) => item.id === itemId);
          if (!campaignItem) return;

          const patchItem: BrandCampaignPatchItemDTO = {
            ...campaignItem.getPatchCampaignItem(),
            isSalesPriceOverridable: !isSalesPriceOverridable,
          };
          return handleUpdateItem(itemId, patchItem);
        }
      : undefined,
  });

  const composeColumns = useCallback(
    (baseColumns: CampaignItemsBaseColumns<BrandCampaignItem>) => {
      return [
        baseColumns.primaryTextColumn,
        baseColumns.mgItemAndExternalNumberColumn,
        baseColumns.supplierColumn,
        brandColumns.isImposedColumn,
        baseColumns.primaryPackageUnitColumn,
        baseColumns.campaignUnitColumn,
        baseColumns.campaignPurchasePriceColumn,
        baseColumns.campaignSalesPriceColumn,
        baseColumns.campaignSalesCoverageColumn,
        ...(showPackageDiscounts
          ? [
              brandColumns.campaignSecondaryDiscountPercentage,
              brandColumns.campaignTertiaryDiscountPercentage,
            ]
          : []),
        baseColumns.purchasePeriodColumnn,
        baseColumns.createdByColumn,
        brandColumns.lastUpdatedByColumn,
        brandColumns.lastUpdatedColumn,
        baseColumns.actionsColumn,
      ];
    },
    [
      brandColumns.campaignSecondaryDiscountPercentage,
      brandColumns.campaignTertiaryDiscountPercentage,
      brandColumns.isImposedColumn,
      brandColumns.lastUpdatedByColumn,
      brandColumns.lastUpdatedColumn,
      showPackageDiscounts,
    ]
  );

  const {
    isLoadingDistribution,
    handleChangeCampaignName,
    handleChangeDescription,
    handleChangeCampaignPeriod,
    handleChangePurchasePeriod,
    handleChangeMandatory,
    handleChangeBrands,
    handleChangeCampaignType,
    handleCampaignDistribution,
  } = useBrandCampaignDetailsActions({ campaign, availableBrands: availableBrandsForChosenTenant });

  return (
    <CampaignDetailsContainerBase
      type="brand"
      campaign={campaign}
      campaignDetailsActions={{
        isLoadingDistribution,
        handleCampaignDistribution,
      }}
      campaignItems={campaignItems}
      fetchItemsStatus={status}
      refetchItems={refetch}
      composeItemsColumns={composeColumns}
      includeVat={includeVat}
      toggleIncludeVat={toggleIncludeVat}
      handleSubmitSalesPrice={handleSubmitSalesPrice}
      handleSubmitPurchasePrice={handleSubmitPurchasePrice}
      handleSubmitCampaignUnit={handleSubmitCampaignUnit}
      handleSubmitPurchasePeriod={handleSubmitPurchasePeriod}
      renderMetadata={() => (
        <BrandCampaignMetadata
          campaign={campaign}
          onSaveCampaignName={
            campaign.isFieldEditable('name') ? handleChangeCampaignName : undefined
          }
          onSaveCampaignPeriod={
            campaign.isFieldEditable('campaignPeriod')
              ? (validFrom, validTo) => handleChangeCampaignPeriod({ validFrom, validTo })
              : undefined
          }
          onSavePurchasePeriod={
            campaign.isFieldEditable('purchasePeriod')
              ? (purchasePeriodFrom, purchasePeriodTo) =>
                  handleChangePurchasePeriod({ purchasePeriodFrom, purchasePeriodTo })
              : undefined
          }
          onSaveMandatory={
            campaign.isFieldEditable('mandatory') ? handleChangeMandatory : undefined
          }
          onSaveDescription={
            campaign.isFieldEditable('description') ? handleChangeDescription : undefined
          }
          onSaveBrands={campaign.isFieldEditable('brands') ? handleChangeBrands : undefined}
          onSaveCampaignType={
            campaign.isFieldEditable('campaignType') ? handleChangeCampaignType : undefined
          }
          availableBrands={availableBrandsForChosenTenant}
        />
      )}
      renderDistributeDialogContent={() => (
        <Stack alignItems="center" gap={2}>
          <Typography textAlign="center">{t('campaign.distribute.description')}</Typography>
          <Stack direction="row" spacing={1}>
            {campaign.brands.map((brand) => (
              <Tooltip title={brand.name} key={brand.brandId}>
                <Image
                  src={brandLogo(brand.brandId).logo}
                  alt={brand.name}
                  height={30}
                  width={30}
                />
              </Tooltip>
            ))}
          </Stack>
        </Stack>
      )}
      renderAddItemsDialog={({ onClose, open }) => (
        <UniversalFormProvider
          schema={addCampaignItemsSchema(brandCampaignItemSchema(t))}
          defaultValues={{ items: {} }}
        >
          <BrandCampaignDetailsAddItemsDialog
            campaignId={campaign.id}
            open={open}
            onClose={onClose}
          />
        </UniversalFormProvider>
      )}
      renderCTA={({ onOpenDistributeCampaignDialog, onOpenAddItemsDialog }) =>
        campaign.isCampaignEditable() && (
          <Stack direction="row" spacing={2}>
            {campaign.releaseStatus !== 'RELEASED' && (
              <Button
                variant="outlined"
                size="small"
                loading={isLoadingDistribution}
                startIcon={<PodcastsIcon fontSize="small" />}
                onClick={onOpenDistributeCampaignDialog}
              >
                {t('campaign.actions.distribute')}
              </Button>
            )}
            <Button
              variant="liquid"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={onOpenAddItemsDialog}
            >
              {t('campaign.actions.addItems')}
            </Button>
          </Stack>
        )
      }
      renderEditCampaignItemsDialog={({ onClose, itemsToEdit }) => (
        <BrandEditCampaignItemsDialog
          campaignId={campaign.id}
          campaignItems={itemsToEdit}
          onClose={onClose}
        />
      )}
    />
  );
};
